// Lib
import React, { useCallback, useState } from 'react';
import classNames from 'classnames';

// Components
import { ImageModalEditorContent, type ImageModalEditorProps } from '../../content/ImageModalEditor';
import ImageModalBackgroundRemovalToast from '../toolbar/ImageModalBackgroundRemovalToast';

// Hooks
import { useImageModalBackgroundRemovalProgress } from './hooks/useImageModalBackgroundRemovalProgress';
import { useImageModalV2BackgroundRemovalState } from './hooks/useImageModalV2BackgroundRemovalState';

// Utils
import { getElementId, getImageProp } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { asObject } from '../../../../../../common/utils/immutableHelper';
import type { MNImageContent } from '../../../../../../common/elements/elementModelTypes';

/**
 * V2 image editor shell: owns background-removal progress, phase state, toast, and toolbar behaviour.
 */
const ImageModalV2Editor: React.FC<ImageModalEditorProps> = (props) => {
    const [editorIsReady, setEditorIsReady] = useState(false);
    const elementId = getElementId(props.element);
    const { backgroundRemovalApiProgress, backgroundRemovalUploadProgress, setBackgroundRemovalProgress } =
        useImageModalBackgroundRemovalProgress(elementId);
    const image = asObject<MNImageContent>(getImageProp(props.element));
    const editorImageLoadKey = JSON.stringify([elementId, image?.original, image?.large, image?.regular, image?.thumb]);
    const backgroundRemovalState = useImageModalV2BackgroundRemovalState({
        editorImageLoadKey,
        editorIsReady,
        backgroundRemovalApiProgress,
        backgroundRemovalUploadProgress,
    });
    const toolbarClassName = classNames('ImageEditorModalToolbar', 'ImageEditorModalToolbar-v2', {
        'toast-visible': backgroundRemovalState.shouldHideToolbar,
    });

    const handleEditorReadyChange = useCallback(
        (ready: boolean) => {
            setEditorIsReady(ready);
            backgroundRemovalState.handleEditorImageReady(ready);
        },
        [backgroundRemovalState.handleEditorImageReady],
    );

    return (
        <ImageModalEditorContent
            {...props}
            editorIsReady={editorIsReady}
            isV2Toolbar
            showImageReplacementWipe={backgroundRemovalState.shouldAnimateEditorImageReplacement}
            showEditorLoadingPreview={backgroundRemovalState.shouldShowEditorLoadingPreview}
            toolbarClassName={toolbarClassName}
            toast={<ImageModalBackgroundRemovalToast toast={backgroundRemovalState.toast} />}
            backgroundRemovalProgress={backgroundRemovalState.buttonProgress}
            backgroundRemovalControlsDisabled={backgroundRemovalState.controlsDisabled}
            onEditorReadyChange={handleEditorReadyChange}
            setBackgroundRemovalProgress={setBackgroundRemovalProgress}
        />
    );
};

export default ImageModalV2Editor;
